import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Building2, TrendingDown, TrendingUp } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getAdminProperty } from '../../services/admin/propertiesService';
import { classifyAdminCallableError } from './adminUserDisplay';
import {
  ADMIN_DATE_PLACEHOLDER,
  adminEnumLabel,
  formatAdminDate,
  formatAdminFinancialValue,
  formatAdminMoney,
  formatAdminPropertyAddress,
  adminOwnerPrimaryLabel,
} from './adminPropertyDisplay';
import AdminPropertyScenarioTree from './AdminPropertyScenarioTree';

const VALUATION_KEYS = ['askingPrice', 'purchasePrice', 'municipalEvaluation'];
const INCOME_KEYS = [
  'annualRent',
  'parkingRevenue',
  'internetRevenue',
  'storageRevenue',
  'otherRevenue',
  'vacancyRate',
];
const EXPENSE_KEYS = [
  'municipalTaxes',
  'schoolTaxes',
  'insurance',
  'electricityHeating',
  'heating',
  'electricity',
  'maintenance',
  'concierge',
  'managementRate',
  'operatingExpenses',
  'otherExpenses',
];
const OPERATING_KEYS = ['netOperatingIncome', 'capRate'];

function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-slate-900">{children}</dd>
    </div>
  );
}

function Section({ title, children, stacked = false }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4">
      <h2 className="text-sm font-semibold text-slate-800">{title}</h2>
      {stacked ? children : <dl className="mt-3 grid gap-3 sm:grid-cols-2">{children}</dl>}
    </section>
  );
}

function StatTile({ label, children }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 text-base font-semibold tabular-nums text-slate-900">{children}</p>
    </div>
  );
}

function LedgerRow({ label, children }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-slate-100 py-1.5 last:border-0">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-sm font-medium tabular-nums text-slate-900">{children}</dd>
    </div>
  );
}

function LedgerColumn({ title, keys, source, total, t, language, accent = 'income' }) {
  const isIncome = accent === 'income';
  return (
    <div
      className={`rounded-lg border border-slate-200 bg-white p-3 ${
        isIncome ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-rose-400'
      }`}
    >
      <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-700">
        {isIncome ? (
          <TrendingUp className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
        ) : (
          <TrendingDown className="h-3.5 w-3.5 text-rose-500" aria-hidden="true" />
        )}
        {title}
      </h3>
      <dl className="mt-2">
        {keys.map((key) => (
          <LedgerRow key={key} label={t(`admin.properties.field.${key}`)}>
            {formatAdminFinancialValue(key, source?.[key], language)}
          </LedgerRow>
        ))}
        <div className="mt-1 flex items-baseline justify-between gap-3 border-t border-slate-200 pt-2">
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            {t('admin.properties.financials.total')}
          </dt>
          <dd className="text-sm font-semibold tabular-nums text-slate-900">
            {formatAdminMoney(total, language)}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function PersonaBadge({ persona, t }) {
  if (!persona) return null;
  const isBroker = persona === 'courtier_immo' || persona === 'courtier_hypo';
  const isLender = persona === 'preteur_prive';
  const tone = isBroker
    ? 'bg-indigo-50 text-indigo-800'
    : isLender
      ? 'bg-amber-50 text-amber-900'
      : 'bg-slate-100 text-slate-700';
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>
      {adminEnumLabel('persona', persona, t)}
    </span>
  );
}

export default function AdminPropertyDetailPage() {
  const { propertyId } = useParams();
  const { t, language } = useLanguage();
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState('loading');
  const [errorKind, setErrorKind] = useState(null);

  const load = useCallback(async () => {
    const id = String(propertyId || '').trim();
    if (!id) {
      setDetail(null);
      setStatus('not-found');
      return;
    }
    setStatus('loading');
    setErrorKind(null);
    try {
      const data = await getAdminProperty(id);
      setDetail(data);
      setStatus('ready');
    } catch (err) {
      setDetail(null);
      const kind = classifyAdminCallableError(err);
      if (kind === 'not-found') {
        setStatus('not-found');
      } else {
        setErrorKind(kind);
        setStatus('error');
      }
    }
  }, [propertyId]);

  useEffect(() => {
    load();
  }, [load]);

  const errorMessage =
    errorKind === 'unavailable'
      ? t('admin.properties.error.unavailable')
      : errorKind === 'internal'
        ? t('admin.properties.error.internal')
        : errorKind === 'permission-denied'
          ? t('admin.properties.error.permission')
          : errorKind === 'invalid-argument'
            ? t('admin.properties.error.invalid')
            : t('admin.properties.detail.error');

  const property = detail?.property || {};
  const owner = detail?.owner || {};
  const brokers = Array.isArray(detail?.brokers) ? detail.brokers : [];
  const shares = Array.isArray(detail?.shares)
    ? detail.shares
    : brokers.map((row) => ({
        uid: row.uid,
        displayName: null,
        email: null,
        persona: null,
        clientId: row.clientId || null,
        dashboard: true,
        shareLink: false,
      }));
  const scenarios = detail?.scenarios || {};
  const ownerUid = typeof owner.uid === 'string' && owner.uid.trim() ? owner.uid.trim() : '';
  const financials = detail?.financials;
  const showScenarioFinancials = financials != null;
  const metadata = detail?.metadata || {};
  const addressLabel = formatAdminPropertyAddress(property) || detail?.id || ADMIN_DATE_PLACEHOLDER;

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <Building2 className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" aria-hidden="true" />
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {t('admin.properties.detail.title')}
              </p>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                {t('admin.properties.readOnly')}
              </span>
            </div>
            {status === 'ready' ? (
              <>
                <h1 className="mt-0.5 text-xl font-semibold text-slate-900">{addressLabel}</h1>
                <p className="mt-1 font-mono text-xs text-slate-500">
                  {t('admin.properties.field.id')} · {detail.id}
                </p>
              </>
            ) : (
              <h1 className="mt-0.5 text-xl font-semibold text-slate-900">{t('admin.properties.detail.title')}</h1>
            )}
          </div>
        </div>
        <Link
          to="/admin/properties"
          className="text-sm font-semibold text-indigo-600 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {t('admin.properties.detail.back')}
        </Link>
      </div>

      {status === 'loading' ? (
        <p className="text-sm text-slate-500" aria-live="polite">
          {t('admin.properties.detail.loading')}
        </p>
      ) : null}

      {status === 'not-found' ? (
        <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          {t('admin.properties.detail.notFound')}
        </p>
      ) : null}

      {status === 'error' ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p>{errorMessage}</p>
          <button
            type="button"
            onClick={load}
            className="mt-2 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            {t('admin.properties.retry')}
          </button>
        </div>
      ) : null}

      {status === 'ready' && detail ? (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <Section title={t('admin.properties.section.building')}>
              <Field label={t('admin.properties.field.units')}>
                {property.numberOfUnits ?? ADMIN_DATE_PLACEHOLDER}
              </Field>
              <Field label={t('admin.properties.field.structureType')}>
                {adminEnumLabel('admin.properties.structureType', property.structureType, t)}
              </Field>
              <Field label={t('admin.properties.field.dealPurpose')}>
                {adminEnumLabel('admin.properties.dealPurpose', property.dealPurpose, t)}
              </Field>
            </Section>

            <Section title={t('admin.properties.section.metadata')}>
              <Field label={t('admin.properties.metadata.firestore')}>
                {t('admin.properties.metadata.createTime')}: {formatAdminDate(metadata.createTime, language)}
                <br />
                {t('admin.properties.metadata.updateTime')}: {formatAdminDate(metadata.updateTime, language)}
              </Field>
              <Field label={t('admin.properties.metadata.application')}>
                {t('admin.properties.metadata.createdAt')}: {formatAdminDate(metadata.createdAt, language)}
                <br />
                {t('admin.properties.metadata.updatedAt')}: {formatAdminDate(metadata.updatedAt, language)}
                <br />
                {t('admin.properties.metadata.brokerSeenAt')}: {formatAdminDate(metadata.brokerSeenAt, language)}
              </Field>
            </Section>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Section title={t('admin.properties.section.owner')}>
              <Field label={t('admin.properties.field.displayName')}>
                {owner.displayName || ADMIN_DATE_PLACEHOLDER}
              </Field>
              <Field label={t('admin.properties.field.email')}>
                {owner.email || ADMIN_DATE_PLACEHOLDER}
              </Field>
              <Field label={t('admin.properties.field.uid')}>
                {ownerUid ? <code className="break-all text-xs">{ownerUid}</code> : ADMIN_DATE_PLACEHOLDER}
              </Field>
              <Field label={t('admin.properties.field.persona')}>
                {adminEnumLabel('persona', owner.persona, t)}
              </Field>
              {ownerUid ? (
                <div className="sm:col-span-2">
                  <Link
                    to={`/admin/users/${encodeURIComponent(ownerUid)}`}
                    className="inline-flex rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {t('admin.properties.viewUsers360')}
                  </Link>
                </div>
              ) : null}
            </Section>

            <Section title={t('admin.properties.section.brokers')} stacked>
              {shares.length === 0 ? (
                <p className="mt-3 text-sm text-slate-500">{t('admin.properties.shares.empty')}</p>
              ) : (
                <ul className="mt-3 divide-y divide-slate-100">
                  {shares.map((share) => {
                    const uid = typeof share.uid === 'string' ? share.uid.trim() : '';
                    const label = adminOwnerPrimaryLabel(share);
                    return (
                      <li key={uid || `${share.clientId}-${label}`} className="py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          {uid ? (
                            <Link
                              to={`/admin/users/${encodeURIComponent(uid)}`}
                              className="text-sm font-medium text-indigo-600 hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                              {label}
                            </Link>
                          ) : (
                            <p className="text-sm font-medium text-slate-900">{label}</p>
                          )}
                          <PersonaBadge persona={share.persona} t={t} />
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {share.dashboard ? (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                              {t('admin.properties.shares.channel.dashboard')}
                            </span>
                          ) : null}
                          {share.shareLink ? (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                              {t('admin.properties.shares.channel.link')}
                            </span>
                          ) : null}
                        </div>
                        {share.clientId ? (
                          <p className="mt-1 text-xs text-slate-500">
                            {t('admin.properties.brokers.clientId')}: {share.clientId}
                          </p>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              )}
            </Section>
          </div>

          {financials == null ? (
            <p className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              {t('admin.properties.financials.hidden')}
            </p>
          ) : (
            <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-4">
              <h2 className="text-sm font-semibold text-slate-800">{t('admin.properties.section.propertyEconomics')}</h2>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                  {t('admin.properties.financials.valuation')}
                </h3>
                <div className="mt-2 grid gap-2 sm:grid-cols-3">
                  {VALUATION_KEYS.map((key) => (
                    <StatTile key={key} label={t(`admin.properties.field.${key}`)}>
                      {formatAdminFinancialValue(key, financials.valuation?.[key], language)}
                    </StatTile>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                  {t('admin.properties.financials.operating')}
                </h3>
                <p className="mt-1 text-xs text-slate-500">{t('admin.properties.financials.operatingHint')}</p>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  {OPERATING_KEYS.map((key) => (
                    <StatTile key={key} label={t(`admin.properties.field.${key}`)}>
                      {formatAdminFinancialValue(key, financials.operatingSnapshot?.[key], language)}
                    </StatTile>
                  ))}
                </div>
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                <LedgerColumn
                  title={t('admin.properties.financials.income')}
                  keys={INCOME_KEYS}
                  source={financials.incomeSummary}
                  total={financials.incomeTotal}
                  t={t}
                  language={language}
                  accent="income"
                />
                <LedgerColumn
                  title={t('admin.properties.financials.expenses')}
                  keys={EXPENSE_KEYS}
                  source={financials.expenseSummary}
                  total={financials.expenseTotal}
                  t={t}
                  language={language}
                  accent="expense"
                />
              </div>
            </section>
          )}

          <AdminPropertyScenarioTree
            scenarios={scenarios}
            ownerUid={ownerUid}
            t={t}
            language={language}
            showFinancials={showScenarioFinancials}
          />
        </>
      ) : null}
    </div>
  );
}
